// Shared date helpers for the Calendar tab (Day / Week / Month views).
// All date math operates on local time — `scheduled_for` is stored UTC
// in Supabase but interpreted as the user's local wall-clock per the
// existing convention in useScheduledMeals.

import { getMeasurementSystem } from "./unitPrefs";

// ─── Day labels ─────────────────────────────────────────────────
// Two parallel arrays so the calendar can render either format
// without recomputing. Indexed by JS getDay() (0 = Sunday).
export const DAY_FULL = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
export const DAY_SHORT = ["SUN","MON","TUE","WED","THU","FRI","SAT"];
export const DAY_LETTER = ["S","M","T","W","T","F","S"];

export const MONTH_FULL = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
export const MONTH_SHORT = [
  "Jan","Feb","Mar","Apr","May","Jun",
  "Jul","Aug","Sep","Oct","Nov","Dec",
];

// ─── Core date math (all local-time) ────────────────────────────

export function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function endOfDay(d) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

export function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

export function addMonths(d, n) {
  const x = new Date(d);
  x.setDate(1);              // avoid month-rollover (Jan 31 + 1 month → Mar 3)
  x.setMonth(x.getMonth() + n);
  return x;
}

// Local-day key. Two dates with the same key are the same calendar
// day in the user's timezone.
export function dayKey(d) {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

export function isSameDay(a, b) {
  return dayKey(a) === dayKey(b);
}

// ─── Week-start (driven by measurement system) ──────────────────
// Per CLAUDE.md adaptability principle: storage is verbatim, the
// display follows user preference. Week-start is derived from the
// existing `getMeasurementSystem()` flag — US users get Sunday-first,
// metric users get Monday-first. No new toggle needed.

export function weekStartIndex(system) {
  const sys = system || getMeasurementSystem();
  return sys === "us" ? 0 : 1;   // 0 = Sunday, 1 = Monday
}

// First day of the week containing `d`, given the system preference.
export function startOfWeek(d, system) {
  const x = startOfDay(d);
  const wsi = weekStartIndex(system);
  // JS getDay() returns 0..6 (Sun..Sat). Distance from week start:
  const diff = (x.getDay() - wsi + 7) % 7;
  return addDays(x, -diff);
}

export function endOfWeek(d, system) {
  return addDays(startOfWeek(d, system), 6);
}

// 7-day array starting at the week containing `d`.
export function weekDays(d, system) {
  const start = startOfWeek(d, system);
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
}

// Day-letter labels in the order this user's week starts. ["S","M",
// "T","W","T","F","S"] for US; ["M","T","W","T","F","S","S"] for
// metric.
export function weekDayLetters(system) {
  const wsi = weekStartIndex(system);
  return Array.from({ length: 7 }, (_, i) => DAY_LETTER[(wsi + i) % 7]);
}
export function weekDayShorts(system) {
  const wsi = weekStartIndex(system);
  return Array.from({ length: 7 }, (_, i) => DAY_SHORT[(wsi + i) % 7]);
}

// ─── Month grid (6 rows × 7 cols, week-aware) ───────────────────
// Always 42 cells regardless of how many days the month has, so
// layout is stable and the grid never reflows when navigating
// months. Out-of-month days come from prev/next month and are
// flagged so the renderer can mute them.

export function monthGrid(d, system) {
  const monthStart = new Date(d.getFullYear(), d.getMonth(), 1);
  const gridStart = startOfWeek(monthStart, system);
  const cells = Array.from({ length: 42 }, (_, i) => {
    const cellDate = addDays(gridStart, i);
    return {
      date: cellDate,
      inMonth: cellDate.getMonth() === monthStart.getMonth(),
      key: dayKey(cellDate),
    };
  });
  return cells;
}

// ─── Time formatting ────────────────────────────────────────────

export function fmtTime(isoOrDate) {
  const d = typeof isoOrDate === "string" ? new Date(isoOrDate) : isoOrDate;
  const h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

// "8 AM" / "12 PM" for hour-grid row labels.
export function fmtHourLabel(h) {
  if (h === 0) return "12 AM";
  if (h === 12) return "12 PM";
  if (h < 12) return `${h} AM`;
  return `${h - 12} PM`;
}

// Long form "Wed · Apr 29" for headers.
export function fmtDayHeader(d) {
  return `${DAY_SHORT[d.getDay()].slice(0, 3).charAt(0) + DAY_SHORT[d.getDay()].slice(1).toLowerCase()} · ${MONTH_SHORT[d.getMonth()]} ${d.getDate()}`;
}

// "April 2026" for month-view header.
export function fmtMonthHeader(d) {
  return `${MONTH_FULL[d.getMonth()]} ${d.getFullYear()}`;
}

// "Apr 27 – May 3" for week-view header.
export function fmtWeekHeader(d, system) {
  const start = startOfWeek(d, system);
  const end = endOfWeek(d, system);
  if (start.getMonth() === end.getMonth()) {
    return `${MONTH_SHORT[start.getMonth()]} ${start.getDate()} – ${end.getDate()}`;
  }
  return `${MONTH_SHORT[start.getMonth()]} ${start.getDate()} – ${MONTH_SHORT[end.getMonth()]} ${end.getDate()}`;
}

// ─── Hour-grid bucketing ────────────────────────────────────────
// Day view's hour grid runs from 6 AM to 10 PM by default. Meals
// (and past cooks) bucket into the hour their `scheduled_for` /
// `cooked_at` falls in. Out-of-range entries (3 AM late-night cook,
// midnight prep) get clamped to the first / last visible hour so
// they don't disappear from the day view entirely.

export const DAY_GRID_HOUR_START = 6;   // 6 AM
export const DAY_GRID_HOUR_END   = 23;  // 11 PM (inclusive — last row is 11 PM,
                                        // covering 11 PM → midnight). Late-night
                                        // dinners + overnight bake starts (brioche
                                        // 11 PM → 9 AM next day) need this row to
                                        // render their head; cross-day backdating
                                        // handles anything past midnight on the
                                        // following day's view.
export const DAY_GRID_HOURS = Array.from(
  { length: DAY_GRID_HOUR_END - DAY_GRID_HOUR_START + 1 },
  (_, i) => DAY_GRID_HOUR_START + i,
);

// Map an event timestamp → its visible hour bucket, clamped to the
// grid's range. Events at 3 AM render at 6 AM (top); events at
// midnight render at 10 PM (bottom). Caller can detect clamping
// via the `clamped` flag if they want to badge the tile.
export function hourBucketFor(ts) {
  const d = ts instanceof Date ? ts : new Date(ts);
  const h = d.getHours();
  if (h < DAY_GRID_HOUR_START) return { hour: DAY_GRID_HOUR_START, clamped: "early" };
  if (h > DAY_GRID_HOUR_END)   return { hour: DAY_GRID_HOUR_END,   clamped: "late"  };
  return { hour: h, clamped: null };
}

// Given a meal/cook list and a target day, return a Map of
// hour → entries on that day. Entries shape:
//   { kind: "meal"|"cook", row, time, clamped }
// Used by Day view (single day) and Week view (7 buckets per day).
export function bucketDayByHour(day, scheduledMeals, pastCooks) {
  const target = dayKey(day);
  const out = new Map();
  for (const h of DAY_GRID_HOURS) out.set(h, []);
  for (const m of (scheduledMeals || [])) {
    const t = new Date(m.scheduled_for);
    if (dayKey(t) !== target) continue;
    const { hour, clamped } = hourBucketFor(t);
    out.get(hour).push({ kind: "meal", row: m, time: t, clamped });
  }
  for (const c of (pastCooks || [])) {
    const t = new Date(c.cooked_at);
    if (dayKey(t) !== target) continue;
    const { hour, clamped } = hourBucketFor(t);
    out.get(hour).push({ kind: "cook", row: c, time: t, clamped });
  }
  // Within an hour bucket, sort by exact time so 8:15 lunch lands
  // before 8:45 cook.
  for (const arr of out.values()) {
    arr.sort((a, b) => a.time.getTime() - b.time.getTime());
  }
  return out;
}

// Aggregate per-day counts for the Month grid's dot indicators.
// Returns { dayKey → { planned, cooked, requested } } so the renderer
// can paint up to N dots per cell with status colors.
export function countMealsByDay(scheduledMeals, pastCooks) {
  const out = new Map();
  const bump = (key, axis) => {
    if (!out.has(key)) out.set(key, { planned: 0, cooked: 0, requested: 0 });
    out.get(key)[axis] += 1;
  };
  for (const m of (scheduledMeals || [])) {
    const k = dayKey(new Date(m.scheduled_for));
    if (m.cook_id == null && !m.from_pantry_row_id) bump(k, "requested");
    else if (m.status === "cooked") bump(k, "cooked");
    else bump(k, "planned");
  }
  for (const c of (pastCooks || [])) {
    bump(dayKey(new Date(c.cooked_at)), "cooked");
  }
  return out;
}

// ─── Per-minute timeline blocks for Day view ─────────────────────
// A "block" is one cooking event positioned and sized by real time:
//   - A planned meal (status="planned" or "requested") with its
//     authored scheduled_for + the recipe's total time = duration.
//   - A cooked planned meal — when a scheduled meal has status
//     "cooked" AND a cook_log exists for the same recipe on the
//     same day, the two are MERGED into one block. Cooked status
//     wins; the cook_log's cooked_at is the actual time the cook
//     happened (may differ from scheduled_for).
//   - A standalone cook_log — cooked without a planned-meal
//     ancestor. Renders at cooked_at with the recipe's typical
//     duration (or a 30-min default).
//
// Real chefs plan per minute — a brioche that takes 6 hours should
// span 6 hours visually, not collapse into a 1-hour block. The
// `durationMinutes` field carries the full extent so CalendarDay
// can render the tile proportional to time.
//
// Returns a flat array sorted by `startMinutes` so render order
// produces stacked-by-time output.
//
// Each block:
//   {
//     id:               unique id (meal.id, cook.id, or merged synth)
//     kind:             "planned" | "cooked" | "requested" | "leftover"
//     meal:             scheduled_meals row (when present)
//     cook:             cook_logs row (when present)
//     time:             Date — the visible start time
//     startMinutes:     minutes since midnight, local
//     durationMinutes:  total time the cook spans
//   }
export function dayTimelineBlocks(day, scheduledMeals, pastCooks, opts = {}) {
  const {
    findRecipe,            // (slug, findUserRecipe?, ownerUserId?) → recipe
    findUserRecipe,        // optional, passed through
    pantry = [],           // for from_pantry_row_id leftover lookups
    defaultDurationMin = 30,
    prepNotifications = [], // prep_notifications rows visible to this user
  } = opts;
  const target = dayKey(day);

  // Match cook_logs to their planned scheduled_meal so we don't
  // double-render. Match key: recipe_slug + cooked-day-key + (if
  // available) the meal's owner user_id. cook_logs.user_id is the
  // chef; scheduled_meal.cook_id is the assigned cook — they should
  // align when the meal was actually cooked.
  const cookByKey = new Map();
  for (const c of (pastCooks || [])) {
    if (!c?.cooked_at || !c.recipe_slug) continue;
    const ck = `${c.recipe_slug}::${dayKey(new Date(c.cooked_at))}`;
    if (!cookByKey.has(ck)) cookByKey.set(ck, []);
    cookByKey.get(ck).push(c);
  }

  const consumedCookIds = new Set();
  const blocks = [];

  // Day-window bounds for cross-day intersection math. A cook block
  // is rendered on EVERY day it touches, with its visible portion
  // clipped to that day's bounds. So a 9.5-hour cook from Sat 11:30 PM
  // to Sun 9 AM produces TWO tiles:
  //   - Saturday: startMinutes=1410, duration=30, continuesIntoNext
  //   - Sunday:   startMinutes=0,    duration=540, continuesFromPrev
  const dayStart = startOfDay(day);
  const dayEnd   = endOfDay(day);

  // Emit one tile for one (segment × day) intersection. Shared between
  // the meal pass and the cook-log pass — every block lives or dies by
  // the same intersection rules. The `extra` argument carries pass-
  // specific fields (kind, meal, cook, idPrefix).
  const pushSegmentBlock = (segment, recipe, extra) => {
    const segStart = segment.start;
    const segEnd   = segment.end;
    const isOvernightDay1 = !!segment.isOvernightDay1;
    const isOvernightDay2 = !!segment.isOvernightDay2;
    // Overnight Day 1's rest naturally trails past midnight (8 PM kneading
    // + 8h cold ferment lands at 4 AM next day). Don't bleed that rest tail
    // onto Day 2's calendar — Day 2 has its own dedicated 8 AM tile. The
    // segment renders ONLY on its home calendar day.
    if (isOvernightDay1 && dayKey(segStart) !== dayKey(day)) return;
    if (segEnd <= dayStart || segStart > dayEnd) return;
    const visibleStart = segStart < dayStart ? dayStart : segStart;
    const visibleEnd   = segEnd   > dayEnd   ? dayEnd   : segEnd;
    const visibleMinutes = Math.max(1, Math.round((visibleEnd.getTime() - visibleStart.getTime()) / 60_000));
    const segDurationMin = Math.max(1, Math.round((segEnd.getTime() - segStart.getTime()) / 60_000));
    // Continuation flags. For non-overnight cooks: same midnight-
    // intersection semantics as before. For overnight day-1: we never
    // continue PAST midnight visually (Day 2 is its own tile, anchored
    // to morning), but we DO advertise "to <nextDay> morning" so the
    // user knows the rest carries on. For overnight day-2: we always
    // advertise "from <prevDay>" because that's where the prep happened.
    const continuesFromPrev = isOvernightDay2 ? true  : segStart < dayStart;
    const continuesIntoNext = isOvernightDay1 ? true  : segEnd   > dayEnd;
    const startMinutes = visibleStart.getHours() * 60 + visibleStart.getMinutes();
    const idSuffix = isOvernightDay1 ? "-d1"
                   : isOvernightDay2 ? "-d2"
                   : "";
    const idTail = (segStart < dayStart && !isOvernightDay2) ? "-cont" : "";

    blocks.push({
      id: `${extra.idPrefix}${idSuffix}${idTail}`,
      kind: extra.kind,
      meal: extra.meal,
      cook: extra.cook,
      recipe,
      // Per-day step bands. Single-day cooks slice by visible window
      // (midnight-clipped). Overnight bakes pre-slice by the day-1 /
      // day-2 step ranges, so each segment carries only its own steps
      // and the visible window further clips inside that.
      steps: stepBandsForWindow(
        recipe,
        segment.start.getTime(),
        visibleStart.getTime(),
        visibleEnd.getTime(),
        { stepRange: segment.stepRange || null },
      ),
      time: visibleStart,
      startMinutes,
      durationMinutes: visibleMinutes,
      // For overnight segments, fullDurationMinutes is the SEGMENT's
      // length, not the recipe's full wall-clock — the user thinks of
      // Day 1 and Day 2 as their own cooking events, not slices of one
      // 14-hour blob.
      fullDurationMinutes: (isOvernightDay1 || isOvernightDay2) ? segDurationMin : extra.fullDurationMinutes,
      continuesFromPrev,
      continuesIntoNext,
      // Optional segment metadata — present only when the recipe has
      // an overnight step. Tile renderers use this to badge "Day 1 of 2"
      // and pick continuation copy ("to Sun morning ›" instead of
      // "to Sun ›").
      segmentIndex: segment.segmentIndex,
      segmentTotal: segment.segmentTotal,
      isOvernightDay1,
      isOvernightDay2,
    });
  };

  // Pass 1 — scheduled meals.
  for (const m of (scheduledMeals || [])) {
    if (!m?.scheduled_for) continue;
    const t = new Date(m.scheduled_for);

    // recipe_snapshot is the point-in-time pin written at schedule
    // time (migration 0164). When present, it wins over any live
    // user_recipes lookup — that pin is what the user actually
    // planned to cook, and an edited / deleted / collision-renamed
    // user_recipes row should not silently shift the meal under
    // them. Falls through to slug lookup for legacy rows that
    // predate the snapshot column and for bundled recipes.
    const recipe = (m.recipe_snapshot && typeof m.recipe_snapshot === "object")
      ? m.recipe_snapshot
      : (m.recipe_slug && findRecipe
        ? findRecipe(m.recipe_slug, findUserRecipe, m.recipe_user_id)
        : null);
    const fullDurationMinutes = pickDurationMin(recipe, defaultDurationMin);
    const overnightDur = getOvernightDurations(recipe);

    const isRequest      = m.cook_id == null && !m.from_pantry_row_id;
    const isLeftover     = !!m.from_pantry_row_id;
    let kind = isLeftover ? "leftover" : isRequest ? "requested" : "planned";
    let cookLink = null;
    const isEatAnchored = !!(m.eat_anchor && !isLeftover);
    // Eat-anchored backdate. Two paths:
    //   - Overnight bake: Day 2 ends at eat time, so cookStart is
    //     (eatTime - day2Min - overnightMin - day1ActiveMin). The user
    //     starts the evening prep that-many-minutes before the loaf
    //     comes out tomorrow morning.
    //   - Single-segment cook (no overnight step): legacy behavior —
    //     cookStart = eatTime - fullDuration.
    let cookOffsetMs = 0;
    if (isEatAnchored) {
      cookOffsetMs = overnightDur
        ? (overnightDur.day1ActiveMin + overnightDur.overnightMin + overnightDur.day2Min) * 60_000
        : fullDurationMinutes * 60_000;
    }
    let cookStart = cookOffsetMs > 0
      ? new Date(t.getTime() - cookOffsetMs)
      : t;

    // If this meal completed (status="cooked"), prefer the actual
    // cook_log time as the block's start so the calendar reflects
    // when the cook REALLY happened, not the planned slot.
    if (m.status === "cooked" && m.recipe_slug) {
      // Search both potential cook days when eat-anchored — the cook
      // could have happened on the backdated day OR the eat day.
      const cookDayKeys = cookOffsetMs > 0
        ? [dayKey(cookStart), dayKey(t)]
        : [dayKey(t)];
      let match = null;
      for (const ck of cookDayKeys.map(d => `${m.recipe_slug}::${d}`)) {
        const candidates = (cookByKey.get(ck) || []).slice();
        if (candidates.length === 0) continue;
        candidates.sort((a, b) =>
          Math.abs(new Date(a.cooked_at).getTime() - t.getTime()) -
          Math.abs(new Date(b.cooked_at).getTime() - t.getTime()),
        );
        match = candidates[0];
        if (match) break;
      }
      if (match) {
        consumedCookIds.add(match.id);
        cookLink  = match;
        cookStart = new Date(match.cooked_at);
        kind = "cooked";
      } else {
        kind = "cooked";
      }
    }

    const segments = segmentsForCook(recipe, cookStart, fullDurationMinutes, {
      eatAnchorTime: isEatAnchored ? t : null,
      day2Hour: Number.isFinite(m.day2_hour) ? m.day2_hour : null,
    });
    for (const seg of segments) {
      pushSegmentBlock(seg, recipe, {
        idPrefix: `meal-${m.id}`,
        kind, meal: m, cook: cookLink,
        fullDurationMinutes,
      });
    }
  }

  // Pass 2 — cook_logs on this day that didn't pair with a meal.
  // Same cross-day intersection logic as Pass 1: a cook that started
  // late and ran past midnight renders on both calendar days.
  for (const c of (pastCooks || [])) {
    if (!c?.cooked_at || consumedCookIds.has(c.id)) continue;
    const cookStart = new Date(c.cooked_at);
    const recipe = c.recipe_slug && findRecipe
      ? findRecipe(c.recipe_slug, findUserRecipe, c.user_id)
      : null;
    const fullDurationMinutes = pickDurationMin(recipe, defaultDurationMin);

    const segments = segmentsForCook(recipe, cookStart, fullDurationMinutes);
    for (const seg of segments) {
      pushSegmentBlock(seg, recipe, {
        idPrefix: `cook-${c.id}`,
        kind: "cooked", meal: null, cook: c,
        fullDurationMinutes,
      });
    }
  }

  // Pass 3 — prep_notifications. Each pending row becomes a thin tile
  // on its delivery day. Distinct kind so CalendarDay can render with
  // a smaller, faded treatment (no step bands, no drag-to-reschedule).
  // Tapping a prep tile opens the source meal's drawer — the prep is
  // an intent that BELONGS to a meal, not a standalone event.
  //
  // Filtered out:
  //   - dismissed rows (user opted out)
  //   - rows with no deliver_at (malformed)
  //
  // Same-day prep (e.g. "T-30m pull steak from fridge" before a 6 PM
  // dinner) DOES render on the calendar — it's part of the visual
  // schedule the user plans around. Earlier this pass skipped same-
  // day prep on the theory the meal drawer was enough; in practice
  // users want to SEE the pull-from-fridge as a tile preceding the
  // cook so the timeline reads as a chain.
  for (const p of (prepNotifications || [])) {
    if (!p?.deliver_at || p.dismissed_at) continue;
    const deliverAt = new Date(p.deliver_at);
    if (dayKey(deliverAt) !== target) continue;
    const sourceMealKey = p.scheduled_meal_id
      ? (scheduledMeals || []).find(m => m.id === p.scheduled_meal_id)
      : null;
    const startMinutes = deliverAt.getHours() * 60 + deliverAt.getMinutes();
    blocks.push({
      id: `prep-${p.id}`,
      kind: "prep",
      meal: sourceMealKey || null,   // tap target — opens this meal's drawer
      cook: null,
      recipe: null,
      steps: null,
      time: deliverAt,
      startMinutes,
      // Prep tiles are point-in-time reminders, not durations. Render
      // as a fixed ~30-min visual slot so they read as "do this around
      // now" without claiming an hour of timeline real estate.
      durationMinutes: 30,
      fullDurationMinutes: 30,
      continuesFromPrev: false,
      continuesIntoNext: false,
      prep: {
        id:        p.id,
        title:     p.title || "Prep reminder",
        body:      p.body || null,
        emoji:     p.emoji || "⏰",
        source:    p.source || "recipe_prep",
        delivered: !!p.delivered_at,
        leadMin:   Number.isFinite(p.lead_minutes) ? p.lead_minutes : null,
      },
    });
  }

  blocks.sort((a, b) => a.startMinutes - b.startMinutes);
  return blocks;
}

// Total seconds for a step. Multi-timer steps (timers[]) sum their
// sub-stage durations; legacy single-timer steps return that value
// directly. Returns 0 for steps with no timing info — callers fall
// back to a minimum visible share.
//
// Exported because every step-duration computation in the app —
// pickDurationMin, stepBandsForWindow, segmentsForCook — needs to
// agree on what "step duration" means. One source of truth.
export function totalStepSeconds(step) {
  if (!step) return 0;
  if (Array.isArray(step.timers) && step.timers.length > 0) {
    return step.timers.reduce(
      (sum, t) => sum + (Number(t?.seconds) > 0 ? Number(t.seconds) : 0),
      0,
    );
  }
  const t = Number(step.timer);
  return Number.isFinite(t) && t > 0 ? t : 0;
}

// Distill a recipe's steps[] into proportional bands the Day view
// can stripe inside the block. Each band carries its own normalized
// weight (sum to 1.0 across all bands), title, icon, and a stable
// palette index — so a 6-hour brioche shows distinct stripes for
// mix / first-proof / shape / second-proof / bake instead of one
// solid 6-hour block.
//
// Steps without a `timer` get a "minimum-share" weight so they
// still render as visible bands (assembly / plating / cooling
// steps). Steps with a timer scale by their timer value so a
// 90-minute proof reads visibly larger than a 5-minute knead.
function pickStepBands(recipe, totalMinutes) {
  const steps = Array.isArray(recipe?.steps) ? recipe.steps : null;
  if (!steps || steps.length === 0) return null;
  // Treat a no-timer step as a default 60s share — visible but
  // small. Active-work steps without a timer field tend to be
  // brief assembly moves.
  const NO_TIMER_DEFAULT_SEC = 60;
  const weights = steps.map(s => {
    const stepSec = totalStepSeconds(s);
    return stepSec > 0 ? stepSec : NO_TIMER_DEFAULT_SEC;
  });
  const total = weights.reduce((a, b) => a + b, 0) || 1;
  return steps.map((s, i) => ({
    index: i,
    weight: weights[i] / total,                      // 0..1
    durationSec: weights[i],
    minutes: Math.round(weights[i] / 60),
    title: s?.title || `Step ${i + 1}`,
    icon: s?.icon || null,
    heat: s?.heat || null,
    timed: totalStepSeconds(s) > 0,
  }));
}

// Step bands for a SPECIFIC visible window inside a longer cook.
// Used by Day-view tiles when an overnight bake spans midnight: each
// day's tile gets only the steps that overlap its visible portion of
// the cook, with weights re-normalized against the visible duration
// (not the recipe's full duration).
//
// Example: 15h brioche starts Sat 8 PM. Steps are mix(1m), dough(4m),
// butter(7m), fold(15m), first-rise(8h), shape(15m), second-rise(2h),
// bake(45m). Saturday's tile (8 PM → midnight = 240m) shows mix +
// dough + butter + fold fully + the FIRST 213m of the first-rise
// (clipped). Sunday's tile (midnight → bake-end) shows the REMAINING
// first-rise minutes + shape + second-rise + bake.
//
// Steps that fall entirely outside the visible window are dropped.
// Partially-visible steps get their `minutes` clipped to the visible
// portion. The `partialStart` / `partialEnd` flags let the renderer
// hint that a step continues from / into another day.
//
// `opts.stepRange` (optional) limits which steps participate — used
// by overnight-bake segments so the Day 1 tile only shows steps
// [0..overnight] and the Day 2 tile only shows steps [overnight+1..end].
// `cookStartMs` is the wall-clock anchor for the FIRST step inside
// stepRange (NOT the recipe's original cook start), so the cursor
// math stays simple inside the segment.
//
// Returns null when:
//   - recipe has no steps
//   - all steps are zero-duration
//   - no steps fall in the visible window
function stepBandsForWindow(recipe, cookStartMs, visibleStartMs, visibleEndMs, opts = {}) {
  const steps = Array.isArray(recipe?.steps) ? recipe.steps : null;
  if (!steps || steps.length === 0) return null;
  const range = opts.stepRange || [0, steps.length - 1];
  const [rangeStart, rangeEnd] = range;
  if (rangeEnd < rangeStart) return null;
  const NO_TIMER_DEFAULT_SEC = 60;
  const visibleDurMin = Math.max(1, Math.round((visibleEndMs - visibleStartMs) / 60_000));
  const out = [];
  let cursor = cookStartMs;
  for (let i = rangeStart; i <= rangeEnd && i < steps.length; i++) {
    const stepSec = totalStepSeconds(steps[i]);
    const stepDurMs = (stepSec > 0 ? stepSec : NO_TIMER_DEFAULT_SEC) * 1000;
    const stepStartMs = cursor;
    const stepEndMs   = cursor + stepDurMs;
    cursor = stepEndMs;
    if (stepEndMs   <= visibleStartMs) continue;     // ended before window
    if (stepStartMs >= visibleEndMs)   break;        // starts after window
    const clipStart = Math.max(stepStartMs, visibleStartMs);
    const clipEnd   = Math.min(stepEndMs,   visibleEndMs);
    const clipDurMin = Math.max(1, Math.round((clipEnd - clipStart) / 60_000));
    const fullDurMin = Math.max(1, Math.round(stepDurMs / 60_000));
    out.push({
      index: i,
      weight: clipDurMin / visibleDurMin,
      durationSec: stepDurMs / 1000,
      minutes: clipDurMin,           // visible minutes (for in-tile label)
      fullMinutes: fullDurMin,       // total step minutes (for drawer / explainer)
      title: steps[i]?.title || `Step ${i + 1}`,
      icon: steps[i]?.icon || null,
      heat: steps[i]?.heat || null,
      timed: stepSec > 0,
      overnight: !!steps[i]?.overnight,
      partialStart: clipStart > stepStartMs,  // continues from previous day
      partialEnd:   clipEnd   < stepEndMs,    // continues into next day
    });
  }
  return out.length > 0 ? out : null;
}

// Find the step that marks the natural Day 1 / Day 2 break for an
// overnight bake. Returns -1 when the recipe has no overnight step,
// signalling "treat this as a normal single-segment cook."
//
// Exported because SchedulePicker, MealDetailDrawer, CookMode, and the
// prep scheduler all branch on "is this an overnight recipe?" — the
// canonical answer should come from one place.
export function findOvernightStepIndex(steps) {
  if (!Array.isArray(steps)) return -1;
  for (let i = 0; i < steps.length; i++) {
    if (steps[i]?.overnight) return i;
  }
  return -1;
}

// Sum of step.timer durations split into the three buckets that matter
// for an overnight bake: pre-overnight active work, the overnight rest
// itself, and post-overnight active work (the bake). Returns null when
// the recipe has no overnight step. Used by callers that need to either
// (a) backdate Day 2 from an eat time (eatTime - day2Min) or
// (b) compute the firing time of the morning push notification.
//
// Times are in MINUTES. Steps without a timer contribute 1 min each
// (matches the no-timer default elsewhere in this module).
export function getOvernightDurations(recipe) {
  const steps = Array.isArray(recipe?.steps) ? recipe.steps : [];
  const overnightIdx = findOvernightStepIndex(steps);
  if (overnightIdx === -1) return null;
  const NO_TIMER_DEFAULT_SEC = 60;
  const minFor = (s) => {
    const stepSec = totalStepSeconds(s);
    return Math.max(1, Math.round(((stepSec > 0 ? stepSec : NO_TIMER_DEFAULT_SEC) * 1000) / 60_000));
  };
  const day1ActiveMin = steps.slice(0, overnightIdx).reduce((a, s) => a + minFor(s), 0);
  const overnightMin  = minFor(steps[overnightIdx]);
  const day2Min       = steps.slice(overnightIdx + 1).reduce((a, s) => a + minFor(s), 0);
  return { overnightIdx, day1ActiveMin, overnightMin, day2Min };
}

// Wall-clock hour at which Day 2 of an overnight bake anchors when
// nothing else dictates the time. 8 AM is the right default — it's
// after typical sleep and gives the bake time to finish before lunch.
// Per-meal `day2_hour` overrides this; eat-anchored meals derive Day 2
// from eat time instead.
export const OVERNIGHT_DAY2_START_HOUR = 8;

// Given a cook's start time + total duration, return the segments that
// describe how it lays out across the calendar. Always at least one
// segment. Recipes with an `overnight: true` step return TWO segments:
// Day 1 = steps[0..overnightIdx] anchored at cookStart; Day 2 =
// steps[overnightIdx+1..end] anchored at 8 AM the next morning (or
// the per-meal override / eat-anchor backdate).
//
// The day-2 anchor is independent of the literal cookStart + step
// timer cursor — that's the whole point. A brioche kneaded at 8 PM
// Sat shouldn't show the bake at 4 AM Sun (cookStart + active +
// overnight rest) just because the rest happens to be 8 hours; the
// user is asleep then. The morning anchor reflects the human reality.
//
// `opts.eatAnchorTime` (Date | null) — when set AND the recipe is an
//   overnight bake, Day 2 ends NEAR this time (so the loaf is out of
//   the oven when the user is ready to eat). day2Start = eatTime -
//   day2Min. Day 1 stays at its (already-backdated upstream) cookStart;
//   the meal pass in dayTimelineBlocks is responsible for backdating
//   cookStart to the previous evening when eat-anchored.
// `opts.day2Hour` (0..23 | null) — per-meal override for the Day 2
//   anchor hour. Ignored when eatAnchorTime is set (eat-anchor wins).
function segmentsForCook(recipe, cookStart, fullDurationMinutes, opts = {}) {
  const steps = Array.isArray(recipe?.steps) ? recipe.steps : [];
  const overnightIdx = findOvernightStepIndex(steps);
  if (overnightIdx === -1 || steps.length === 0) {
    // No overnight split — single segment, behaves identically to the
    // pre-feature cook (cookStart → cookStart + fullDuration, all
    // steps in scope).
    return [{
      segmentIndex: 0, segmentTotal: 1,
      start: cookStart,
      end: new Date(cookStart.getTime() + fullDurationMinutes * 60_000),
      stepRange: null,
    }];
  }
  const NO_TIMER_DEFAULT_SEC = 60;
  const stepDurMs = steps.map(s => {
    const stepSec = totalStepSeconds(s);
    return (stepSec > 0 ? stepSec : NO_TIMER_DEFAULT_SEC) * 1000;
  });
  // Day 1 includes the overnight step itself — the rest visualizes as
  // the closing band of the evening. The user reads "knead, shape, then
  // it ferments overnight" off Saturday's tile.
  const day1ActiveMs = stepDurMs.slice(0, overnightIdx).reduce((a, b) => a + b, 0);
  const day1RestMs   = stepDurMs[overnightIdx] || 0;
  const day2DurMs    = stepDurMs.slice(overnightIdx + 1).reduce((a, b) => a + b, 0);
  const day1Start    = new Date(cookStart);
  const day1End      = new Date(cookStart.getTime() + day1ActiveMs + day1RestMs);

  // Day 2 anchor — three modes:
  //   1. eat-anchored:   day2Start = eatTime - day2DurMs
  //                      (loaf comes out at eatTime; user eats fresh)
  //   2. day2Hour override: anchor next-day calendar at the chosen hour
  //   3. default:        anchor next-day calendar at 8 AM
  let day2Start;
  if (opts.eatAnchorTime instanceof Date) {
    day2Start = new Date(opts.eatAnchorTime.getTime() - day2DurMs);
  } else {
    const hour = (Number.isFinite(opts.day2Hour) && opts.day2Hour >= 0 && opts.day2Hour <= 23)
      ? Math.floor(opts.day2Hour)
      : OVERNIGHT_DAY2_START_HOUR;
    const d = new Date(cookStart);
    d.setDate(d.getDate() + 1);
    d.setHours(hour, 0, 0, 0);
    day2Start = d;
  }

  return [
    {
      segmentIndex: 0, segmentTotal: 2,
      start: day1Start, end: day1End,
      stepRange: [0, overnightIdx],
      isOvernightDay1: true,
    },
    {
      segmentIndex: 1, segmentTotal: 2,
      start: day2Start,
      end: new Date(day2Start.getTime() + day2DurMs),
      stepRange: [overnightIdx + 1, steps.length - 1],
      isOvernightDay2: true,
    },
  ];
}

// Total wall-clock time for a recipe in minutes. Prefer sum of step
// timers — captures passive waits (proofs, marinades, rests, sous-
// vide) that recipe.time.cook excludes by convention.
//
// recipe.time.cook is ACTIVE-WORK time only. A brioche authored as
// `time: { prep: 30, cook: 45 }` reports 75 min by that metric, but
// its steps[] include two 90-min proofs — real elapsed time is
// ~4 hours. The Day-view block height has to reflect wall-clock,
// not active work, or long-elapsed recipes silently collapse to
// stubby blocks (the user's "is there a 75-minute max" report).
//
// Fallback chain:
//   1. Sum of step.timer (seconds → minutes) + recipe.time.prep as
//      pre-cook buffer. This is the most accurate wall-clock figure.
//   2. recipe.time.prep + recipe.time.cook — for recipes whose
//      steps don't carry timers.
//   3. caller-provided default — for recipes with neither.
export function pickDurationMin(recipe, fallback = 30) {
  if (!recipe) return fallback;
  // Authored total. Used as both an upper-floor and as the fallback
  // when steps don't carry timers.
  const authoredPrep = Number(recipe?.time?.prep) || 0;
  const authoredCook = Number(recipe?.time?.cook) || 0;
  const authoredTotal = authoredPrep + authoredCook;
  if (Array.isArray(recipe.steps) && recipe.steps.length > 0) {
    const stepSec = recipe.steps.reduce(
      (sum, s) => sum + totalStepSeconds(s),
      0,
    );
    if (stepSec > 0) {
      const stepMin = Math.round(stepSec / 60);
      // Use the LARGER of step-derived total and author-claimed total —
      // never sum them. Step timers usually already include prep work
      // (most recipe authors put "knead 8 min" / "sauté onions 5 min"
      // INSIDE step.timer); adding recipe.time.prep on top double-
      // counts and inflates the eat-anchor backdate. If the author
      // claimed more than the step timers carry, that excess is the
      // unstamped buffer (mise-en-place, gathering ingredients) the
      // steps don't track — keep it via Math.max.
      return Math.max(stepMin, authoredTotal);
    }
  }
  if (Number.isFinite(authoredTotal) && authoredTotal > 0) return authoredTotal;
  return fallback;
}

// Build a Date at a specific hour on `day` for the SchedulePicker
// initial-time seed when the user taps an empty hour row.
export function dateAtHour(day, hour) {
  const x = startOfDay(day);
  x.setHours(hour, 0, 0, 0);
  return x;
}

// ─── Display helpers shared across calendar surfaces ─────────────

// Format a minute count into a compact human-readable label.
// Below 60 → "45m". 60+ → "1h 10m" (or "2h" when minutes are zero).
export function formatMinutes(min) {
  const m = Math.max(0, Math.round(Number(min) || 0));
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m - h * 60;
  return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
}

// Slug → Title Case fallback for recipes whose title didn't resolve.
// "ultra-rich-brioche-loaf" → "Ultra Rich Brioche Loaf".
export function prettifySlug(slug) {
  if (!slug) return null;
  return String(slug)
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim() || null;
}

// Step-band palette — eight distinct hues that read on the cream
// background. Cycled by step index so adjacent bands stay visually
// distinct (terracotta → leaf → sky → rose → gold → violet → ember
// → teal repeats only after 8 steps).
export const STEP_PALETTE = [
  { fg: "#A86B2B", bg: "rgba(168,107,43,0.22)", border: "rgba(168,107,43,0.45)" },
  { fg: "#3F8A6E", bg: "rgba(63,138,110,0.22)", border: "rgba(63,138,110,0.45)" },
  { fg: "#6F8FBF", bg: "rgba(111,143,191,0.20)", border: "rgba(111,143,191,0.45)" },
  { fg: "#B45A4F", bg: "rgba(180,90,79,0.22)",   border: "rgba(180,90,79,0.45)"   },
  { fg: "#9C7B2C", bg: "rgba(212,166,55,0.22)",  border: "rgba(212,166,55,0.45)"  },
  { fg: "#7A5BA1", bg: "rgba(122,91,161,0.20)",  border: "rgba(122,91,161,0.42)"  },
  { fg: "#A26B45", bg: "rgba(162,107,69,0.22)",  border: "rgba(162,107,69,0.45)"  },
  { fg: "#3E8F8C", bg: "rgba(62,143,140,0.20)",  border: "rgba(62,143,140,0.45)"  },
];
export function paletteFor(index) {
  return STEP_PALETTE[((Number(index) || 0) % STEP_PALETTE.length + STEP_PALETTE.length) % STEP_PALETTE.length];
}
